<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Card from 'primevue/card'
import Button from 'primevue/button'
import { useAppToast } from '@/shared/lib/useAppToast'

import ProductTypeFormDialog from '@/modules/admin/products/product-types/components/ProductTypeFormDialog.vue'
import DeleteProductTypeConfirmDialog from '@/modules/admin/products/product-types/components/DeleteProductTypeConfirmDialog.vue'

import {
  getProductTypeById,
  updateProductType,
  deleteProductType
} from '@/modules/admin/products/product-types/api/productTypes.service'

const route = useRoute()
const router = useRouter()
const toast = useAppToast()

const productType = ref(null)
const loading = ref(true)

const editDialogVisible = ref(false)
const deleteDialogVisible = ref(false)

const submittingProductType = ref(false)
const deletingProductType = ref(false)
const productTypeFieldErrors = ref({})

async function loadProductType() {
  try {
    loading.value = true
    const id = route.params.id
    const data = await getProductTypeById(id)

    productType.value = {
      id: data.id ?? data.Id,
      name: data.name ?? data.Name,
      collectionName: data.collectionName ?? data.CollectionName,
      modelCode: data.modelCode ?? data.ModelCode,
      description: data.description ?? data.Description,
      brandId: data.brandId ?? data.BrandId,
      brandName: data.brandName ?? data.BrandName ?? '-',
      categoryId: data.categoryId ?? data.CategoryId,
      categoryName: data.categoryName ?? data.CategoryName ?? '-',
      isActive: data.isActive ?? data.IsActive ?? false,
      attributes:
        data.productTypeAttributes?.map((a) => ({
          id: a.productAttribute?.id ?? a.productAttribute?.Id,
          name: a.productAttribute?.name ?? a.productAttribute?.Name,
          values:
            a.productAttribute?.values ??
            a.productAttribute?.Values ??
            a.values ??
            []
        })) ??
        data.attributes ??
        []
    }
  } catch (error) {
    productType.value = null
    toast.error(error.message || 'Не удалось загрузить тип продукта')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/admin/product-types')
}

function openEditDialog() {
  productTypeFieldErrors.value = {}
  editDialogVisible.value = true
}

function closeEditDialog() {
  editDialogVisible.value = false
  productTypeFieldErrors.value = {}
}

function openDeleteDialog() {
  deleteDialogVisible.value = true
}

function closeDeleteDialog() {
  if (deletingProductType.value) return
  deleteDialogVisible.value = false
}

async function handleSubmitProductType(formData) {
  try {
    submittingProductType.value = true
    productTypeFieldErrors.value = {}

    const payload = {
      Id: productType.value?.id,
      Name: formData.name,
      CollectionName: formData.collectionName,
      ModelCode: formData.modelCode,
      Description: formData.description,
      BrandId: formData.brandId,
      CategoryId: formData.categoryId,
      IsActive: formData.isActive,
      AttributeIds: [...(formData.attributeIds ?? [])],
      AttributeDefinitions: formData.attributeDefinitions ?? null
    }

    await updateProductType(payload)
    toast.success('Тип продукта обновлён')

    closeEditDialog()
    await loadProductType()
  } catch (error) {
    productTypeFieldErrors.value = error.fieldErrors || {}
    toast.error(error.message || 'Ошибка при сохранении типа продукта')
  } finally {
    submittingProductType.value = false
  }
}

async function handleDeleteProductType() {
  if (deletingProductType.value) return
  try {
    if (!productType.value?.id) return

    deletingProductType.value = true

    await deleteProductType(productType.value.id)
    toast.success('Тип продукта удалён')

    router.push('/admin/product-types')
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении типа продукта')
  } finally {
    deletingProductType.value = false
    closeDeleteDialog()
  }
}

onMounted(() => {
  loadProductType()
})
</script>

<template>
  <div class="product-type-details-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <div v-if="productType && !loading" class="top-actions">
          <Button
            label="Редактировать"
            icon="pi pi-pencil"
            class="edit-btn"
            outlined
            @click="openEditDialog"
          />
          <Button
            label="Удалить"
            icon="pi pi-trash"
            class="delete-btn"
            outlined
            :loading="deletingProductType"
            :disabled="deletingProductType"
            @click="openDeleteDialog"
          />
        </div>
      </div>

      <div class="page-header">
        <div>
          <h1>Детали типа продукта</h1>
          <p>Просмотр основной информации, атрибутов и состояния</p>
        </div>
      </div>

      <div v-if="loading" class="state-block">
        Загрузка...
      </div>

      <div v-else-if="!productType" class="state-block">
        Тип продукта не найден
      </div>

      <Card v-else class="details-card">
        <template #content>
          <div class="details-content">
            <div class="details-main">
              <div class="title-row">
                <h2 class="product-type-name">{{ productType.name }}</h2>
                <span class="status-chip" :class="{ 'status-chip--inactive': !productType.isActive }">
                  {{ productType.isActive ? 'Активен' : 'Неактивен' }}
                </span>
              </div>

              <div class="info-grid">
                <div class="info-block info-block--full">
                  <div class="info-label">Описание</div>
                  <div class="info-value info-value--description">
                    {{ productType.description ?? 'Нет описания' }}
                  </div>
                </div>

                <div class="info-block">
                  <div class="info-label">Бренд</div>
                  <div class="info-value">{{ productType.brandName ?? '-' }}</div>
                </div>

                <div class="info-block">
                  <div class="info-label">Категория</div>
                  <div class="info-value">{{ productType.categoryName ?? '-' }}</div>
                </div>
              </div>
            </div>

            <div class="attributes-section">
              <div class="section-title">Атрибуты</div>

              <div
                v-if="!productType.attributes || productType.attributes.length === 0"
                class="empty-attributes"
              >
                Атрибуты не добавлены
              </div>

              <div v-else class="attributes-list">
                <div
                  v-for="attr in productType.attributes"
                  :key="attr.id"
                  class="attribute-card"
                >
                  <div class="attribute-name">
                    {{ attr.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <ProductTypeFormDialog
        :visible="editDialogVisible"
        mode="edit"
        :product-type="productType"
        :submitting="submittingProductType"
        :field-errors="productTypeFieldErrors"
        @close="closeEditDialog"
        @submit="handleSubmitProductType"
      />

      <DeleteProductTypeConfirmDialog
        :visible="deleteDialogVisible"
        :product-type="productType"
        :deleting="deletingProductType"
        @close="closeDeleteDialog"
        @confirm="handleDeleteProductType"
      />
    </div>
  </div>
</template>

<style scoped>
.product-type-details-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.top-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.details-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.details-card.p-card) {
  background: #ffffff;
}

:deep(.details-card .p-card-content) {
  padding: 28px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.details-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.details-main {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.product-type-name {
  margin: 0;
  font-size: 32px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(196, 18, 23, 0.06);
  border: 1px solid rgba(196, 18, 23, 0.12);
  color: #9f1015;
  font-size: 13px;
  font-weight: 600;
}

.status-chip--inactive {
  background: #f4f4f4;
  border-color: #e4e4e4;
  color: #6f6f6f;
}

.info-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 16px;
}

.info-block {
  padding: 18px;
  border: 1px solid #eeeeee;
  border-radius: 18px;
  background: #fcfcfc;
}

.info-block--full {
  min-width: 0;
  grid-column: 1 / -1;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: #767676;
  margin-bottom: 10px;
}

.info-value {
  font-size: 16px;
  color: #111111;
  line-height: 1.6;
}

.info-value--description {
  overflow-wrap: anywhere;
  color: #444444;
}

.attributes-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #111111;
  letter-spacing: -0.02em;
}

.empty-attributes {
  padding: 20px;
  border: 1px dashed #d7d7d7;
  border-radius: 16px;
  color: #777777;
  background: #fafafa;
}

.attributes-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.attribute-card {
  border: 1px solid #ececec;
  border-radius: 18px;
  padding: 18px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.attribute-name {
  font-size: 17px;
  font-weight: 400;
  color: #111111;
}

.attribute-values {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.value-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 11px;
  border-radius: 999px;
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #4d4d4d;
  font-size: 13px;
}

.value-chip--empty {
  color: #8b8b8b;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.edit-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.edit-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.delete-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.delete-btn.p-button:hover) {
  background: #fff8f8;
  border-color: #c41217;
  color: #c41217;
}

@media (max-width: 900px) {
  .info-grid,
  .attributes-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .top-actions {
    width: 100%;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .product-type-name {
    font-size: 28px;
  }
}
</style>
